import { z } from "zod";

interface ValidationContext {
  hasPhotoFromApi?: boolean;
  hasEnrollmentFromApi?: boolean;
}

export function createPersonalInfoSchema(
  isStudent: boolean = false,
  context?: ValidationContext
) {
  return z
    .object({
      iin: z
        .string()
        .length(12, { message: "validation.personal.iin_length" })
        .regex(/^\d+$/, { message: "validation.personal.iin_digits_only" }),

      name: z.string().min(1, { message: "validation.personal.name_required" }),
      surname: z.string().min(1, { message: "validation.personal.surname_required" }),
      father_name: z.string().optional().or(z.literal("")),

      date_of_birth: z
        .date({ message: "validation.personal.date_required" })
        .nullable()
        .refine((d) => d instanceof Date, {
          message: "validation.personal.date_invalid",
        })
        .refine((d) => !d || d <= new Date(), {
          message: "validation.personal.date_future",
        }),

      gender: z.string().min(1, { message: "validation.personal.gender_required" }),
      family_status: z.string().min(1, { message: "validation.personal.family_required" }),
      city_of_birth_id: z.number().nullable().optional(),
      city_of_birth_other: z.string().nullable().optional(),
      nationality: z.string().min(1, { message: "validation.personal.nationality_required" }),
      citizenship: z.string().min(1, { message: "validation.personal.citizenship_required" }),

      is_resident: z.boolean(),
      is_student: z.boolean(),
      enrollment_start_date: z.string().nullable().optional(),
      enrollment_end_date: z.string().nullable().optional(),

      photo_file: z.instanceof(File).optional().nullable(),
      enrollment_verification_file: z.instanceof(File).optional().nullable(),
    })
    .superRefine((data, ctx) => {
      const hasPhotoInForm = data.photo_file !== null && data.photo_file !== undefined;
      const hasPhotoFromApi = context?.hasPhotoFromApi ?? false;

      if (!hasPhotoInForm && !hasPhotoFromApi) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.personal.photo_required",
          path: ["photo_file"],
        });
      }

      if (isStudent) {
        const hasEnrollmentInForm =
          data.enrollment_verification_file !== null && data.enrollment_verification_file !== undefined;
        const hasEnrollmentFromApi = context?.hasEnrollmentFromApi ?? false;

        if (!hasEnrollmentInForm && !hasEnrollmentFromApi) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.personal.enrollment_file_required",
            path: ["enrollment_verification_file"],
          });
        }

        if (!data.enrollment_start_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.personal.enrollment_start_date_required",
            path: ["enrollment_start_date"],
          });
        }

        if (!data.enrollment_end_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.personal.enrollment_end_date_required",
            path: ["enrollment_end_date"],
          });
        }

        if (data.enrollment_start_date && data.enrollment_end_date && data.enrollment_start_date >= data.enrollment_end_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "validation.personal.enrollment_end_after_start",
            path: ["enrollment_end_date"],
          });
        }
      }

      const hasCityId = data.city_of_birth_id !== null && data.city_of_birth_id !== undefined && data.city_of_birth_id > 0;
      const hasCityOther = data.city_of_birth_other !== null && data.city_of_birth_other !== undefined && data.city_of_birth_other.trim() !== "";

      if (!hasCityId && !hasCityOther) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.personal.city_required",
          path: ["city_of_birth_id"],
        });
      }

      if (hasCityId && hasCityOther) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "validation.personal.city_mutually_exclusive",
          path: ["city_of_birth_id"],
        });
      }
    });
}

export const personalInfoSchema = createPersonalInfoSchema();

export type PersonalInfoFormInput = z.input<ReturnType<typeof createPersonalInfoSchema>>;
export type PersonalInfoFormValues = z.output<ReturnType<typeof createPersonalInfoSchema>>;
